import { Box, Button, CircularProgress, IconButton, LinearProgress, Stack, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AutoAwesomeMotionOutlinedIcon from "@mui/icons-material/AutoAwesomeMotionOutlined";
import ProductGlyph from "./icons/ProductGlyph";
import IntegrationIcons from "./icons/IntegrationIcons";
import type { Product } from "./types";
import ContextMeshScore from "./ContextMeshScore";
import ProductBatchGenerationStatus, { isActiveProductBatch } from "./ProductBatchGenerationStatus";
import type { TrackedBatch } from "../batchGeneration";
import AnalyzeContextButton from "./AnalyzeContextButton";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

interface ProductCardProps {
    product: Product;
    onOpen?: (product: Product) => void;
    onEdit?: (product: Product) => void;
    onDelete?: (product: Product) => void;
    onSync?: (product: Product) => void;
    onGenerateAll?: (product: Product) => void;
    onAnalyzeContext?: (product: Product) => void;
    onRetryBuild?: (product: Product) => void;
    retryPending?: boolean;
    retryError?: string | null;
    onOpenBatchDetails?: () => void;
    batch?: TrackedBatch;
    batchPending?: boolean;
}

interface CountChipProps {
    icon: string;
    value: number;
    label: string;
}

const CountChip = ({ icon, value, label }: CountChipProps) => {
    const theme = useTheme();
    return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
            <Box component="span" aria-hidden="true" sx={{ color: theme.palette.text.secondary, fontSize: 12, lineHeight: 1 }}>
                {icon}
            </Box>
            <Typography
                component="span"
                sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    fontSize: 11.5,
                    lineHeight: 1.2
                }}
            >
                {value} {label}
            </Typography>
        </Stack>
    );
};

const ProductCard = ({
    product,
    onOpen,
    onEdit,
    onDelete,
    onSync,
    onGenerateAll,
    onAnalyzeContext,
    onRetryBuild,
    retryPending = false,
    retryError,
    onOpenBatchDetails,
    batch,
    batchPending = false
}: ProductCardProps) => {
    const theme = useTheme();
    const isProcessing = product.cardState === "processing" && product.build != null;
    const batchActive = isActiveProductBatch(batch);
    const batchVisible = Boolean(batch || batchPending);
    const generateDisabled = isProcessing || batchActive || batchPending;
    const generateTooltip = isProcessing
        ? "Available when product processing finishes"
        : batchPending
          ? "Checking Context Mesh requirements"
          : batchActive
            ? "Batch generation is already in progress"
            : "Generate All Work Products";
    const canAnalyzeContext = product.contextMeshStatus === "unavailable"
        && (product.build == null || product.build.state === "completed")
        && onAnalyzeContext != null;
    const canRetryBuild = product.build?.state === "failed" && product.build.retryable === true && onRetryBuild != null;
    const buildActivities = product.build?.activities ?? [];
    const activeActivity =
        buildActivities.find(activity => activity.status === "running") ??
        buildActivities.find(activity => activity.status === "queued" || activity.status === "not_started") ??
        buildActivities.find(activity => activity.status === "failed") ??
        buildActivities[buildActivities.length - 1];

    return (
        <Box
            role={onOpen ? "button" : undefined}
            aria-label={onOpen ? (isProcessing ? `View build progress for ${product.name}` : `Open ${product.name}`) : undefined}
            tabIndex={onOpen ? 0 : undefined}
            onClick={onOpen ? () => onOpen(product) : undefined}
            onKeyDown={event => {
                if (onOpen && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    onOpen(product);
                }
            }}
            sx={{
                cursor: onOpen ? "pointer" : "default",
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                background: theme.palette.background.paper,
                p: 2,
                minHeight: 178,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                transition: "border-color .15s, box-shadow .15s",
                ...(onOpen
                    ? {
                          "&:hover": {
                              borderColor: alpha(theme.palette.primary.main, 0.4),
                              boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)"
                          },
                          "&:focus-visible": {
                              outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                              outlineOffset: 2
                          }
                      }
                    : {})
            }}
        >
            <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        backgroundColor: alpha(product.color, 0.13)
                    }}
                >
                    <ProductGlyph color={product.color} size={17} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: 14.5,
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            letterSpacing: "-0.005em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {product.name}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 12.5,
                            color: theme.palette.text.secondary,
                            lineHeight: 1.4,
                            mt: 0.25,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden"
                        }}
                    >
                        {product.sub}
                    </Typography>
                </Box>
                {onGenerateAll && (
                    <Tooltip title={generateTooltip} arrow>
                        <span>
                            <IconButton
                                size="small"
                                aria-label={`Generate all work products for ${product.name}`}
                                disabled={generateDisabled}
                                onClick={event => {
                                    event.stopPropagation();
                                    onGenerateAll(product);
                                }}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    color: theme.palette.primary.main,
                                    flex: "0 0 auto",
                                    "&:hover": {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.09)
                                    }
                                }}
                            >
                                <AutoAwesomeMotionOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {onSync && (
                    <Tooltip title={isProcessing ? "Available when processing finishes" : "Sync to Jira/DevOps"} arrow>
                        <span>
                            <IconButton
                                size="small"
                                aria-label="Sync product"
                                disabled={isProcessing}
                                onClick={event => {
                                    event.stopPropagation();
                                    onSync(product);
                                }}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    color: theme.palette.text.secondary,
                                    flex: "0 0 auto",
                                    "&:hover": {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.07),
                                        color: theme.palette.primary.main
                                    }
                                }}
                            >
                                <SyncOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {onEdit && <Tooltip title={isProcessing ? "Available when processing finishes" : "Edit product"} arrow>
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Edit product"
                            disabled={isProcessing}
                            onClick={event => {
                                event.stopPropagation();
                                onEdit(product);
                            }}
                            sx={{
                                width: 32,
                                height: 32,
                                color: theme.palette.text.secondary,
                                flex: "0 0 auto",
                                "&:hover": {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.07),
                                    color: theme.palette.primary.main
                                }
                            }}
                        >
                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>}
            </Stack>

            {isProcessing ? (
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                        <Box
                            component="span"
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: product.build?.has_failures ? "error.main" : "primary.main",
                                flex: "0 0 auto"
                            }}
                        />
                        <Typography
                            sx={{
                                flex: 1,
                                fontSize: 10.5,
                                fontWeight: 800,
                                color: product.build?.has_failures ? "error.main" : "primary.main",
                                textTransform: "uppercase",
                                letterSpacing: "0.07em"
                            }}
                        >
                            {product.build?.has_failures ? "Needs attention" : "Processing"}
                        </Typography>
                        {activeActivity?.progress != null && (
                            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, fontWeight: 700 }}>
                                {activeActivity.progress}%
                            </Typography>
                        )}
                    </Stack>
                    <Typography
                        sx={{
                            mb: 0.75,
                            minWidth: 0,
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: activeActivity?.status === "failed" ? "error.main" : "text.primary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {activeActivity?.label || "Building product context"}
                    </Typography>
                    {retryError && (
                        <Typography role="alert" sx={{ mb: 0.75, fontSize: 11, color: "error.main" }}>
                            {retryError}
                        </Typography>
                    )}
                    {activeActivity?.status !== "failed" && (
                        <LinearProgress
                            variant={activeActivity?.progress == null ? "indeterminate" : "determinate"}
                            value={activeActivity?.progress ?? undefined}
                            sx={{ height: 4, borderRadius: 3 }}
                        />
                    )}
                </Box>
            ) : batchVisible ? (
                <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end" }}>
                    <ProductBatchGenerationStatus productName={product.name} batch={batch} checking={batchPending} onOpenDetails={onOpenBatchDetails} />
                </Box>
            ) : (
                <>
                    <Box sx={{ flex: 1 }} />
                    <Stack spacing={0.5} data-testid="product-card-metadata">
                        <Stack direction="row" alignItems="center" spacing={0.625}>
                            <CalendarTodayOutlinedIcon aria-hidden="true" sx={{ fontSize: 11, color: theme.palette.text.secondary }} />
                            <Typography sx={{ fontSize: 11.5, color: theme.palette.text.secondary, lineHeight: 1.35 }}>Updated {product.updated}</Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1.75}>
                            <CountChip icon="◎" value={product.features} label={product.features === 1 ? "feature" : "features"} />
                            <CountChip icon="▤" value={product.stories} label={product.stories === 1 ? "story" : "stories"} />
                            <Box sx={{ flex: 1 }} />
                            <IntegrationIcons ids={product.integrations} size={14} />
                        </Stack>
                    </Stack>
                </>
            )}

            {isProcessing && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        pt: 1.25,
                        borderTop: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Typography sx={{ fontSize: 11.5, fontWeight: 650, color: product.build?.has_failures ? "error.main" : "primary.main" }}>
                        {product.build?.has_failures ? "Build needs attention" : "View details"}
                    </Typography>
                    {canRetryBuild && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={retryPending}
                            startIcon={retryPending ? <CircularProgress size={13} color="inherit" /> : <ReplayRoundedIcon fontSize="small" />}
                            aria-label={`Retry product build for ${product.name}`}
                            onClick={event => {
                                event.stopPropagation();
                                onRetryBuild?.(product);
                            }}
                            sx={{ minWidth: 86, py: 0.25, fontSize: 11, fontWeight: 700 }}
                        >
                            {retryPending ? "Retrying…" : "Retry"}
                        </Button>
                    )}
                </Stack>
            )}

            <Stack direction="row" alignItems="center" sx={{ pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                <ContextMeshScore analysis={product.contextMeshAnalysis} score={product.contextMeshScore} status={product.contextMeshStatus} buildState={product.build?.state} />
                {canAnalyzeContext && (
                    <AnalyzeContextButton productName={product.name} onAnalyze={() => onAnalyzeContext?.(product)} />
                )}
                <Box sx={{ flex: 1 }} />
                {onDelete && <Tooltip title="Delete product" arrow>
                    <IconButton
                        aria-label={`Delete ${product.name}`}
                        size="small"
                        onClick={event => {
                            event.stopPropagation();
                            onDelete(product);
                        }}
                        sx={{
                            width: 32,
                            height: 32,
                            color: theme.palette.text.secondary,
                            "&:hover": {
                                backgroundColor: alpha(theme.palette.error.main, 0.08),
                                color: theme.palette.error.main
                            }
                        }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>}
            </Stack>
        </Box>
    );
};

export default ProductCard;
